import React from "react";

import { Link } from "react-router-dom";

import { useRoutes } from "utils/routes";

import Grid from "ui/Grid";
import ImpactRenderer, { Impact } from "ui/ImpactRenderer";

import styles from "./style.module.scss";

import { GetIndexAdvisorIssuesQuery } from "../gql/Query.generated";

type IssuesType = GetIndexAdvisorIssuesQuery["getIssues"];
type SchemaTablesType = GetIndexAdvisorIssuesQuery["getSchemaTables"];
type SchemaTableType = GetIndexAdvisorIssuesQuery["getSchemaTables"][number];

import { getTableName } from "features/IndexAdvisor/IndexAdvisorOverview";
import {
  formatApproximateNumber,
  formatBytes,
  formatNumber,
  formatNumberWithThreshold,
} from "utils/format";
import Popover from "ui/Popover";
import { makeFilter } from "utils/filter";
import pluralize from "pluralize";

const MissingIndexList: React.FunctionComponent<{
  searchTerm: string;
  issues: IssuesType;
  tables: SchemaTablesType;
}> = ({ searchTerm, issues, tables }) => {
  const { databaseIssue } = useRoutes();
  const statsByTable = tables.reduce<{ [key: string]: SchemaTableType }>(
    (byTableMap, table) => {
      byTableMap[table.id] = table;
      return byTableMap;
    },
    {},
  );

  const gridData = issues
    .map((issue) => {
      const tableId = issue.groupingKey.table;
      const tableName = getTableName(issue);
      const details = issue.details;
      const improvement = details.aggregate_improvement;
      const tableSize = statsByTable[tableId]?.lastStats?.dataSizeBytes;
      const writeOverhead = details.index_write_overhead;
      const tableWritesPerMin = details.table_writes_per_min;

      return {
        id: issue.id,
        databaseId: issue.databaseId,
        improvement,
        scans: details.scan_count,
        queries: details.query_count,
        insights: details.insight_count,
        tableName,
        tableSize,
        tableWritesPerMin,
        writeOverhead,
      };
    })
    .filter(makeFilter(searchTerm, "tableName"));

  return (
    <Grid
      striped
      data={gridData}
      defaultSortBy="improvement"
      columns={[
        {
          header: "Impact",
          field: "improvement",
          width: "80px",
          className: "flex justify-center",
          defaultSortOrder: "DESC",
          renderer: function Impact({
            rowData: { id, scans, databaseId },
            fieldData,
          }) {
            if (fieldData == null) {
              return (
                <Popover content="Could not calculate impact for this insight">
                  <Link
                    to={databaseIssue(
                      databaseId,
                      id,
                      "index_advisor/indexing_engine",
                    )}
                  >
                    <ImpactRenderer impact={undefined} />
                  </Link>
                </Popover>
              );
            }
            const roundedImprovement = formatApproximateNumber(fieldData);
            const impact = calculateImpact(fieldData);
            return (
              <Popover
                content={
                  <>
                    <div className={styles.costImprovement}>
                      <strong>{roundedImprovement}× cost improvement</strong>
                    </div>
                    <div>
                      This is a weighted average of the estimated cost
                      improvement across {pluralize("scan", scans, true)},
                      weighting by avg scans / minute
                    </div>
                  </>
                }
              >
                <Link
                  to={databaseIssue(
                    databaseId,
                    id,
                    "index_advisor/indexing_engine",
                  )}
                >
                  <ImpactRenderer impact={impact} />
                </Link>
              </Popover>
            );
          },
        },
        {
          header: "Table",
          field: "tableName",
          width: "3fr",
          renderer: function IssueLinkCell({
            rowData: { id, databaseId },
            fieldData,
          }) {
            return (
              <Link
                to={databaseIssue(
                  databaseId,
                  id,
                  "index_advisor/indexing_engine",
                )}
              >
                {fieldData}
              </Link>
            );
          },
        },
        {
          header: "Affected Indexes",
          field: "insights",
          width: "140px",
          nullValue: "n/a",
          defaultSortOrder: "DESC",
          style: "number",
        },
        {
          header: "Queries",
          field: "queries",
          width: "100px",
          nullValue: "n/a",
          defaultSortOrder: "DESC",
          style: "number",
        },
        {
          header: "Table Size",
          field: "tableSize",
          width: "120px",
          defaultSortOrder: "DESC",
          nullValue: "n/a",
          renderer: ({ fieldData }) => formatBytes(fieldData),
          style: "number",
        },
        {
          header: "Table Writes / Min",
          field: "tableWritesPerMin",
          width: "150px",
          defaultSortOrder: "DESC",
          nullValue: "n/a",
          renderer: ({ fieldData }) => formatNumberWithThreshold(fieldData, 3),
          style: "number",
        },
        {
          header: "Index Write Overhead",
          field: "writeOverhead",
          width: "180px",
          defaultSortOrder: "ASC",
          nullValue: "n/a",
          renderer: function IndexWriteOverhead({ fieldData }) {
            return (
              <span className={styles.writeOverhead}>
                +{formatNumber(fieldData, 2)}
              </span>
            );
          },
          style: "number",
        },
      ]}
    />
  );
};

function calculateImpact(costImprovement: number): Impact {
  return costImprovement > 10_000
    ? 5
    : costImprovement > 1_000
    ? 4
    : costImprovement > 100
    ? 3
    : costImprovement > 10
    ? 2
    : 1;
}

export default MissingIndexList;
